import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { useHeader } from '../context/HeaderContext';
import api from '../services/api';
import socketService from '../services/socket';
import {
    Activity, Play, RotateCw, Smartphone, Key, Upload, Trash2, Code, ShieldAlert,
    Terminal, Globe, Navigation, Lock, Unlock, Camera, RefreshCw, Check, X, Info, HelpCircle, Copy
} from 'lucide-react';
import './InteractiveSession.css';

const CopyableValue = ({ value, className = '' }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        if (!value) return;
        navigator.clipboard.writeText(value.toString());
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <span className={`copy-value-wrapper ${className}`}>
            <span>{value}</span>
            <button
                onClick={handleCopy}
                className="copy-btn"
                title="Copy to clipboard"
            >
                {copied ? <Check size={12} style={{ color: '#22c55e' }} /> : <Copy size={12} />}
            </button>
        </span>
    );
};


function InteractiveSession() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const { currentWorkspace } = useWorkspace();
    const { setHeaderLeft } = useHeader();

    // Session states
    const [session, setSession] = useState(null);
    const [status, setStatus] = useState('initializing');
    const [statusMessage, setStatusMessage] = useState('Connecting to emulator environment...');
    const [progressLogs, setProgressLogs] = useState([]);
    const [currentScreenshot, setCurrentScreenshot] = useState(null);
    const [recordedSteps, setRecordedSteps] = useState([]);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [activeTab, setActiveTab] = useState('steps'); // 'steps' | 'info'

    // Modal / action configurations
    const [activeModal, setActiveModal] = useState(null); // 'openUrl', 'language', 'media', 'key', 'adb', 'location', 'getUi', 'convert'
    const [modalInputs, setModalInputs] = useState({});
    const [isSendingAction, setIsSendingAction] = useState(false);
    const [actionResult, setActionResult] = useState(null);

    // Coordinate state for swipe gestures
    const [touchStart, setTouchStart] = useState(null);
    const [orientation, setOrientation] = useState('portrait'); // 'portrait' | 'landscape'
    const [keyboardFocused, setKeyboardFocused] = useState(false); // true = laptop keys route to emulator
    const screenRef = useRef(null);

    // Update custom header name on mount
    useEffect(() => {
        if (setHeaderLeft) {
            setHeaderLeft(
                <div className="flex items-center gap-2">
                    <span className="live-badge">LIVE</span>
                    <span className="font-semibold text-lg">Interactive Emulator Session</span>
                </div>
            );
        }
    }, [setHeaderLeft]);

    // 1. Fetch Session Status on Load
    const fetchSessionDetails = useCallback(async () => {
        try {
            const response = await api.get(`/interactive-sessions/${sessionId}`);
            setSession(response.data);
            setStatus(response.data.status);
            setRecordedSteps(response.data.recordedSteps || []);
        } catch (error) {
            console.error('Failed to load session details:', error);
            setStatus('error');
            setStatusMessage('Failed to fetch session. Please ensure it exists.');
        }
    }, [sessionId]);

    useEffect(() => {
        fetchSessionDetails();
    }, [fetchSessionDetails]);

    // 2. Connect WebSockets
    useEffect(() => {
        socketService.connect();
        const socket = socketService.socket;

        if (socket) {
            socket.emit('interactive-session:join', sessionId);

            socket.on('session:status', (data) => {
                setStatus(data.status);
                if (data.message) setStatusMessage(data.message);
            });

            socket.on('session:progress', (data) => {
                setProgressLogs(prev => [...prev, data.message]);
                setStatusMessage(data.message);
            });

            socket.on('session:screenshot', (data) => {
                setCurrentScreenshot(`data:image/png;base64,${data.base64}`);
            });

            socket.on('session:steps', (data) => {
                setRecordedSteps(data.steps || []);
            });

            socket.on('session:action-result', (data) => {
                setActionResult(data);
                setIsSendingAction(false);
                // Sync orientation from backend response
                if (data.action === 'rotate' && data.result?.orientation) {
                    setOrientation(data.result.orientation.toLowerCase() === 'landscape' ? 'landscape' : 'portrait');
                }
                setTimeout(() => setActionResult(null), 3000);
            });
        }

        return () => {
            if (socket) {
                socket.emit('interactive-session:leave', sessionId);
                socket.off('session:status');
                socket.off('session:progress');
                socket.off('session:screenshot');
                socket.off('session:steps');
                socket.off('session:action-result');
            }
        };
    }, [sessionId]);

    // 5. Physical keyboard capture — routes laptop keystrokes into the emulator
    useEffect(() => {
        const IGNORED_KEYS = new Set([
            'Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'NumLock', 'ScrollLock',
            'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
            'ContextMenu', 'Dead', 'Unidentified', 'Process'
        ]);

        const handleKeyDown = (e) => {
            // Only active during a live session with keyboard focus on the device
            if (status !== 'active' || !keyboardFocused || activeModal) return;

            // Skip if a browser input/textarea/select/contenteditable is focused
            const active = document.activeElement;
            if (active && (
                active.tagName === 'INPUT' ||
                active.tagName === 'TEXTAREA' ||
                active.tagName === 'SELECT' ||
                active.isContentEditable
            )) return;

            // Skip modifier-only presses and browser shortcuts (Ctrl/Cmd + key)
            if (IGNORED_KEYS.has(e.key)) return;
            if (e.ctrlKey || e.metaKey) return;

            // Prevent browser default for special keys (e.g. Backspace navigating back)
            if (['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                'Delete', 'Home', 'End', 'PageUp', 'PageDown', 'Enter', 'Escape'].includes(e.key)) {
                e.preventDefault();
            }

            // Single printable char or mapped special key — send to emulator
            if (socketService.socket) {
                socketService.socket.emit('interactive-session:action', {
                    sessionId,
                    actionData: { action: 'typeKeys', key: e.key }
                });
            }
        };

        // Dismiss keyboard focus when clicking outside the device screen
        const handleDocClick = (e) => {
            if (screenRef.current && !screenRef.current.contains(e.target)) {
                setKeyboardFocused(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleDocClick);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleDocClick);
        };
    }, [status, keyboardFocused, activeModal, sessionId]);

    // 3. Uptime counter (heartbeat is driven by user actions, not time — see emitAction)
    useEffect(() => {
        let interval = null;
        if (status === 'active') {
            interval = setInterval(() => {
                setElapsedSeconds(prev => prev + 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [status]);

    // 4. Translate client mouse events to emulator percentages
    const handleMouseDown = (e) => {
        if (status !== 'active') return;
        // Clicking the device screen activates keyboard capture
        setKeyboardFocused(true);
        const rect = screenRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setTouchStart({ x, y, time: Date.now() });
    };

    const handleMouseUp = (e) => {
        if (!touchStart || status !== 'active') return;
        const rect = screenRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const duration = Date.now() - touchStart.time;

        const distance = Math.sqrt(Math.pow(x - touchStart.x, 2) + Math.pow(y - touchStart.y, 2));

        const width = rect.width;
        const height = rect.height;

        if (distance < 10 || duration < 200) {
            // Tap gesture
            const tapX = Math.round((touchStart.x / width) * 1000);
            const tapY = Math.round((touchStart.y / height) * 1000);
            emitAction({
                action: 'tap',
                target: { x: tapX, y: tapY }
            });
        } else {
            // Swipe gesture
            const x1 = Math.round((touchStart.x / width) * 1000);
            const y1 = Math.round((touchStart.y / height) * 1000);
            const x2 = Math.round((x / width) * 1000);
            const y2 = Math.round((y / height) * 1000);
            emitAction({
                action: 'swipe',
                target: { x1, y1, x2, y2 }
            });
        }
        setTouchStart(null);
    };

    // Helper to send actions via Socket.
    // lastActiveAt is reset server-side inside handleAction for every dispatched
    // event, so no separate heartbeat HTTP call is needed here.
    const emitAction = (actionData) => {
        if (socketService.socket) {
            setIsSendingAction(true);
            socketService.socket.emit('interactive-session:action', {
                sessionId,
                actionData
            });
        }
    };

    // Terminate session manually
    const handleStopSession = async () => {
        try {
            await api.post(`/interactive-sessions/${sessionId}/stop`);
            setStatus('stopped');
        } catch (error) {
            console.error('Failed to stop interactive session:', error);
        }
    };

    // Convert recorded steps to a TestGoal and TestExecution
    const handleConvertSession = async () => {
        try {
            const response = await api.post(`/interactive-sessions/${sessionId}/convert`, {
                goalText: modalInputs.goalText
            });
            setActiveModal(null);
            navigate(`/goals/${response.data.testGoalId}`);
        } catch (error) {
            console.error('Failed to convert session:', error);
            alert('Failed to convert session: ' + (error.response?.data?.error || error.message));
        }
    };

    // File selection to Base64
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                setModalInputs(prev => ({
                    ...prev,
                    filename: file.name,
                    base64Data: reader.result.split(',')[1] // remove data prefix
                }));
            };
        }
    };

    // Format seconds to readable format
    const formatTime = (totalSeconds) => {
        const hrs = Math.floor(totalSeconds / 3600);
        const mins = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;
        return [
            hrs > 0 ? hrs : null,
            mins.toString().padStart(2, '0'),
            secs.toString().padStart(2, '0')
        ].filter(Boolean).join(':');
    };

    // Render loading log entries
    const renderInitializingView = () => (
        <div className="init-container">
            <div className="init-card">
                <div className="spinner"></div>
                <h2>Provisioning Live Device</h2>
                <p className="status-msg">{statusMessage}</p>
                <div className="logs-box">
                    {progressLogs.map((log, idx) => (
                        <div key={idx} className="log-line">➔ {log}</div>
                    ))}
                </div>
            </div>
        </div>
    );

    // Main component layout
    return (
        <div className="interactive-session-container">
            {/* Top Toolbar / Status Bar */}
            <div className="session-status-bar">
                <div className="status-item">
                    <span className={`status-indicator ${status}`}></span>
                    <span className="status-label capitalize">{status}</span>
                </div>
                {status === 'active' && (
                    <div className="status-metrics">
                        <div className="metric">
                            <span className="metric-label">Uptime:</span>
                            <span className="metric-value font-mono">{formatTime(elapsedSeconds)}</span>
                        </div>
                        <div className="metric">
                            <span className="metric-label">Steps Recorded:</span>
                            <span className="metric-value font-mono">{recordedSteps.length}</span>
                        </div>
                        <div className="metric">
                            <span className="metric-label">Platform:</span>
                            <span className="metric-value font-mono capitalize">{session?.platform}</span>
                        </div>
                    </div>
                )}
                <div className="status-actions">
                    {status === 'active' && (
                        <>
                            <button
                                className="btn btn-success flex items-center gap-1 btn-sm"
                                onClick={() => setActiveModal('convert')}
                            >
                                <Check size={16} /> Save to Test Goal
                            </button>
                            <button
                                className="btn btn-danger flex items-center gap-1 btn-sm"
                                onClick={handleStopSession}
                            >
                                <X size={16} /> End Session
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Content Area */}
            {status === 'initializing' ? (
                renderInitializingView()
            ) : (
                <div className="session-workspace">
                    {/* Left: Recorded Steps / Info Panel */}
                    <div className="recorded-steps-panel flex flex-col h-full">
                        {status !== 'active' ? (
                            <div className="sidebar-tabs">
                                <button
                                    className={`sidebar-tab-btn ${activeTab === 'steps' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('steps')}
                                >
                                    Steps
                                </button>
                                <button
                                    className={`sidebar-tab-btn ${activeTab === 'info' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('info')}
                                >
                                    Info
                                </button>
                            </div>
                        ) : (
                            <h3>Recorded Steps</h3>
                        )}

                        <div className="tab-content flex-1 overflow-y-auto">
                            {activeTab === 'steps' ? (
                                recordedSteps.length === 0 ? (
                                    <div className="empty-steps">
                                        <Info size={24} />
                                        <p>Perform gestures directly on the device preview to record test steps.</p>
                                    </div>
                                ) : (
                                    <div className="steps-list">
                                        {recordedSteps.map((step, idx) => (
                                            <div key={step.id || idx} className="step-card">
                                                <span className="step-num font-mono">{idx + 1}</span>
                                                <div className="step-details">
                                                    <div className="step-action font-semibold capitalize">{step.action}</div>
                                                    {step.target && (
                                                        <div className="step-target font-mono">
                                                            {step.target.x !== undefined ? `Target: (${step.target.x}, ${step.target.y})` : `Swipe: (${step.target.x1}, ${step.target.y1}) ➔ (${step.target.x2}, ${step.target.y2})`}
                                                        </div>
                                                    )}
                                                    {step.value && <div className="step-value font-mono">Value: {step.value}</div>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )
                            ) : (
                                <div className="info-panel">
                                    <div className="info-section">
                                        <h4>Execution Details</h4>
                                        <div className="info-item">
                                            <span className="info-label">Session ID</span>
                                            <CopyableValue value={session?.id} className="info-value font-mono" />
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Date</span>
                                            <span className="info-value">{session?.createdAt ? new Date(session.createdAt).toLocaleString() : '-'}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Duration</span>
                                            <span className="info-value">
                                                {session?.durationSeconds != null ?
                                                    `${Math.floor(session.durationSeconds / 60)}m ${session.durationSeconds % 60}s`
                                                    : '-'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="info-section">
                                        <h4>Device Information</h4>
                                        <div className="info-item">
                                            <span className="info-label">Platform</span>
                                            <CopyableValue value={session?.platform} className="info-value capitalize" />
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Profile</span>
                                            <CopyableValue value={session?.deviceProfile || 'Unknown'} className="info-value font-mono" />
                                        </div>
                                    </div>

                                    <div className="info-section">
                                        <h4>App Info</h4>
                                        <div className="info-item">
                                            <span className="info-label">App ID</span>
                                            <CopyableValue value={session?.appId} className="info-value font-mono" />
                                        </div>
                                        {session?.app && (
                                            <>
                                                <div className="info-item">
                                                    <span className="info-label">Name</span>
                                                    <CopyableValue value={session.app.displayName || session.app.name} className="info-value" />
                                                </div>
                                                <div className="info-item">
                                                    <span className="info-label">Version</span>
                                                    <CopyableValue value={session.app.version || '-'} className="info-value" />
                                                </div>
                                                <div className="info-item">
                                                    <span className="info-label">Package / Bundle</span>
                                                    <CopyableValue value={session.app.packageName || '-'} className="info-value font-mono" />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Center: Device Emulator Preview / Video */}
                    <div className="device-preview-column flex flex-col items-center justify-center">
                        {status !== 'active' && session?.videoUrl ? (
                            <div className="video-player-wrapper w-full max-w-md bg-black rounded overflow-hidden shadow-lg" style={{ maxHeight: '85vh' }}>
                                <video
                                    src={session.videoUrl}
                                    controls
                                    className="w-full h-auto max-h-[85vh] object-contain"
                                    style={{ borderRadius: '8px' }}
                                />
                            </div>
                        ) : (
                            <div className="device-wrapper">
                                <div className={`device-shell ${session?.platform} ${orientation}`}>
                                    <div
                                        className="device-screen"
                                        ref={screenRef}
                                        onMouseDown={handleMouseDown}
                                        onMouseUp={handleMouseUp}
                                        style={{ cursor: isSendingAction ? 'wait' : 'crosshair' }}
                                    >
                                        {/* Keyboard capture indicator */}
                                        {/* {keyboardFocused && (
                                            <div className="keyboard-capture-badge">
                                                ⌨️ Keyboard active
                                            </div>
                                        )} */}
                                        {currentScreenshot ? (
                                            <img src={currentScreenshot} alt="Device screenshot" draggable="false" />
                                        ) : (
                                            <div className="screen-fallback">
                                                {status === 'active' ? (
                                                    <>
                                                        <Loader2 className="spinner" />
                                                        <span>Waiting for video feed...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <ShieldAlert size={32} style={{ marginBottom: '8px', opacity: 0.7 }} />
                                                        <span>Session ended. No video recorded.</span>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right: Floating Actions Toolbar */}
                    {status === 'active' && (
                        <div className="floating-toolbar-wrapper">
                            <div className="floating-toolbar">
                                <h4>Toolbar Actions</h4>
                                <div className="toolbar-grid">
                                    <button title="Simulate Biometry" onClick={() => emitAction({ action: 'biometry', value: true })}>
                                        <Lock size={18} /> Biometry
                                    </button>
                                    <button title="Keep Alive" onClick={() => emitAction({ action: 'keepAlive' })}>
                                        <Activity size={18} /> Keep Alive
                                    </button>
                                    <button title="Open URL / Deep Link" onClick={() => setActiveModal('openUrl')}>
                                        <Play size={18} /> Open URL
                                    </button>
                                    <button title="Reinstall App" onClick={() => emitAction({ action: 'reinstallApp' })}>
                                        <RefreshCw size={18} /> Reinstall
                                    </button>
                                    <button title="Rotate Device" onClick={() => {
                                        // Optimistically toggle the device shell orientation immediately
                                        setOrientation(prev => prev === 'portrait' ? 'landscape' : 'portrait');
                                        emitAction({ action: 'rotate' });
                                    }}>
                                        <RotateCw size={18} /> Rotate
                                    </button>
                                    <button title="Download Screenshot" onClick={() => {
                                        if (currentScreenshot) {
                                            const link = document.createElement('a');
                                            link.href = currentScreenshot;
                                            link.download = `screenshot-${sessionId}-${Date.now()}.png`;
                                            link.click();
                                        }
                                    }}>
                                        <Camera size={18} /> Screenshot
                                    </button>
                                    <button title="Shake Device" onClick={() => emitAction({ action: 'shake' })}>
                                        <Smartphone size={18} /> Shake
                                    </button>
                                    <button title="Set Language" onClick={() => setActiveModal('language')}>
                                        <Globe size={18} /> Language
                                    </button>
                                    <button title="Upload Media File" onClick={() => setActiveModal('media')}>
                                        <Upload size={18} /> Upload Media
                                    </button>
                                    <button title="Press Key" onClick={() => setActiveModal('key')}>
                                        <Key size={18} /> Key Press
                                    </button>
                                    {session?.platform === 'android' && (
                                        <button title="Execute ADB Shell" onClick={() => setActiveModal('adb')}>
                                            <Terminal size={18} /> ADB Shell
                                        </button>
                                    )}
                                    <button title="Inspect UI (JSON)" onClick={() => {
                                        emitAction({ action: 'getUI' });
                                        setActiveModal('getUi');
                                    }}>
                                        <Code size={18} /> Get UI JSON
                                    </button>
                                    <button title="Restart App" onClick={() => emitAction({ action: 'restartApp' })}>
                                        <RefreshCw size={18} /> Restart App
                                    </button>
                                    <button title="Set Geo Location" onClick={() => setActiveModal('location')}>
                                        <Navigation size={18} /> Set Location
                                    </button>
                                    <button title="Lock / Unlock Toggle" onClick={() => emitAction({ action: 'lockUnlock' })}>
                                        <Unlock size={18} /> Toggle Lock
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Custom Modals */}
            {activeModal && (
                <div className="modal-backdrop">
                    <div className="modal-content-card">
                        <div className="modal-header">
                            <h3>
                                {activeModal === 'openUrl' && 'Open Browser URL / Deep Link'}
                                {activeModal === 'language' && 'Set System Language'}
                                {activeModal === 'media' && 'Upload Media to Device'}
                                {activeModal === 'key' && 'Press Virtual Key'}
                                {activeModal === 'adb' && 'Execute ADB Shell Command'}
                                {activeModal === 'location' && 'Set Mock GPS Coordinates'}
                                {activeModal === 'getUi' && 'UI Inspect Result'}
                                {activeModal === 'convert' && 'Convert to Test Goal'}
                            </h3>
                            <button className="close-btn" onClick={() => setActiveModal(null)}><X size={18} /></button>
                        </div>
                        <div className="modal-body">
                            {/* Open URL */}
                            {activeModal === 'openUrl' && (
                                <div className="form-group">
                                    <label>Destination Link / Deep Link URL</label>
                                    <input
                                        type="text"
                                        placeholder="https://example.com or myapp://dashboard"
                                        value={modalInputs.url || ''}
                                        onChange={(e) => setModalInputs({ ...modalInputs, url: e.target.value })}
                                    />
                                </div>
                            )}

                            {/* System Language */}
                            {activeModal === 'language' && (
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Language Code</label>
                                        <input
                                            type="text"
                                            placeholder="en"
                                            value={modalInputs.language || ''}
                                            onChange={(e) => setModalInputs({ ...modalInputs, language: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Locale/Region Code</label>
                                        <input
                                            type="text"
                                            placeholder="US"
                                            value={modalInputs.locale || ''}
                                            onChange={(e) => setModalInputs({ ...modalInputs, locale: e.target.value })}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Upload Media */}
                            {activeModal === 'media' && (
                                <div className="form-group">
                                    <label>Select Media File (Image, Video, Audio)</label>
                                    <input
                                        type="file"
                                        accept="image/*,video/*,audio/*"
                                        onChange={handleFileChange}
                                    />
                                    {modalInputs.filename && (
                                        <div className="file-info text-sm text-green-500">Selected: {modalInputs.filename}</div>
                                    )}
                                </div>
                            )}

                            {/* Press Key */}
                            {activeModal === 'key' && (
                                <div className="form-group">
                                    <label>Choose Key Event</label>
                                    <select
                                        value={modalInputs.key || ''}
                                        onChange={(e) => setModalInputs({ ...modalInputs, key: e.target.value })}
                                    >
                                        <option value="">Select a key...</option>
                                        <option value="Home">Home</option>
                                        <option value="Back">Back</option>
                                        <option value="VolumeUp">Volume Up</option>
                                        <option value="VolumeDown">Volume Down</option>
                                        <option value="Power">Power</option>
                                        <option value="Enter">Enter</option>
                                    </select>
                                </div>
                            )}

                            {/* ADB Command */}
                            {activeModal === 'adb' && (
                                <div className="form-group">
                                    <label>ADB Command / Arguments</label>
                                    <input
                                        type="text"
                                        placeholder="input keyevent 4 (Back)"
                                        value={modalInputs.adbCommand || ''}
                                        onChange={(e) => setModalInputs({ ...modalInputs, adbCommand: e.target.value })}
                                    />
                                </div>
                            )}

                            {/* Set GPS Location */}
                            {activeModal === 'location' && (
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Latitude</label>
                                        <input
                                            type="number"
                                            step="0.000001"
                                            placeholder="37.7749"
                                            value={modalInputs.latitude || ''}
                                            onChange={(e) => setModalInputs({ ...modalInputs, latitude: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Longitude</label>
                                        <input
                                            type="number"
                                            step="0.000001"
                                            placeholder="-122.4194"
                                            value={modalInputs.longitude || ''}
                                            onChange={(e) => setModalInputs({ ...modalInputs, longitude: e.target.value })}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* UI Inspect JSON */}
                            {activeModal === 'getUi' && (
                                <div className="ui-json-container font-mono">
                                    {actionResult?.action === 'getUI' && actionResult.result?.ui ? (
                                        <pre>{JSON.stringify(actionResult.result.ui, null, 2)}</pre>
                                    ) : (
                                        <div className="loading flex items-center justify-center p-8">
                                            <Loader2 className="spinner" /> Retrieving layout XML hierarchy...
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Convert to Test Goal */}
                            {activeModal === 'convert' && (
                                <div className="form-group">
                                    <label>Goal Text / Expected Behavior</label>
                                    <textarea
                                        rows={4}
                                        placeholder="Describe what this recorded test accomplishes (e.g. Log in, select settings, and update email address)."
                                        value={modalInputs.goalText || ''}
                                        onChange={(e) => setModalInputs({ ...modalInputs, goalText: e.target.value })}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Modal Footer Actions */}
                        {activeModal !== 'getUi' && (
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setActiveModal(null)}>Cancel</button>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => {
                                        if (activeModal === 'openUrl') {
                                            emitAction({ action: 'openUrl', value: modalInputs.url });
                                        } else if (activeModal === 'language') {
                                            emitAction({ action: 'setLanguage', language: modalInputs.language, locale: modalInputs.locale });
                                        } else if (activeModal === 'media') {
                                            emitAction({ action: 'uploadMedia', filename: modalInputs.filename, base64Data: modalInputs.base64Data });
                                        } else if (activeModal === 'key') {
                                            emitAction({ action: 'pressKey', value: modalInputs.key });
                                        } else if (activeModal === 'adb') {
                                            emitAction({ action: 'executeAdb', command: modalInputs.adbCommand });
                                        } else if (activeModal === 'location') {
                                            emitAction({ action: 'setLocation', latitude: parseFloat(modalInputs.latitude), longitude: parseFloat(modalInputs.longitude) });
                                        } else if (activeModal === 'convert') {
                                            handleConvertSession();
                                            return;
                                        }
                                        setActiveModal(null);
                                    }}
                                    disabled={activeModal === 'media' && !modalInputs.base64Data}
                                >
                                    Confirm
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// Simple loader helper
const Loader2 = ({ className }) => <RefreshCw className={className} />;

export default InteractiveSession;
